import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkle, Article, Trash, ArrowSquareOut, CircleNotch } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBlogPage() {
  const [topic, setTopic] = useState('');
  const [keywords, setKeywords] = useState('');
  const [posts, setPosts] = useState([]);
  const [jobs, setJobs] = useState({}); // {jobId: {status, slug, title, error}}
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);

  const loadPosts = async () => {
    try {
      const r = await axios.get(`${API}/blog`);
      setPosts(r.data || []);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  // Poll active jobs
  useEffect(() => {
    const activeJobs = Object.entries(jobs).filter(([, j]) => j.status === 'pending' || j.status === 'running');
    if (activeJobs.length === 0) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const pending = Object.entries(jobs).filter(([, j]) => j.status === 'pending' || j.status === 'running');
      for (const [jobId] of pending) {
        try {
          const r = await axios.get(`${API}/blog/jobs/${jobId}`);
          setJobs(prev => ({ ...prev, [jobId]: r.data }));
          if (r.data.status === 'done') {
            toast.success(`Published: ${r.data.title}`);
            loadPosts();
          } else if (r.data.status === 'failed') {
            toast.error(`Generation failed: ${r.data.error || 'unknown error'}`);
          }
        } catch (e) {
          // ignore transient errors
        }
      }
    }, 4000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [jobs]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/blog/generate`, { topic: topic.trim(), keywords: keywords.trim() });
      const jobId = r.data.job_id;
      setJobs(prev => ({ ...prev, [jobId]: { status: 'pending', topic: topic.trim() } }));
      toast.success('Article generation started. This usually takes 30-90 seconds.');
      setTopic('');
      setKeywords('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start generation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete post "${slug}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/blog/${slug}`);
      toast.success('Post deleted');
      loadPosts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const pendingJobs = Object.entries(jobs).filter(([, j]) => j.status === 'pending' || j.status === 'running');

  return (
    <div className="max-w-5xl space-y-8" data-testid="admin-blog-page">
      <div>
        <h1 className="text-2xl font-black tracking-tight text-zinc-950" style={{ fontFamily: 'var(--font-heading)' }}>Blog</h1>
        <p className="text-sm text-zinc-500 mt-1">Generate SEO-optimized articles with GPT-4o. Published posts appear at <Link to="/blog" className="text-[#0052FF] underline" target="_blank" rel="noreferrer">/blog</Link>.</p>
      </div>

      {/* Generate form */}
      <form onSubmit={handleGenerate} className="bg-white border border-zinc-200 rounded-sm p-6 space-y-4" data-testid="blog-generate-form">
        <div className="flex items-center gap-2 pb-2 border-b border-zinc-100">
          <Sparkle size={18} weight="duotone" className="text-[#0052FF]" />
          <h2 className="text-sm font-bold text-zinc-900">Generate new article</h2>
        </div>
        <div>
          <label className="block text-xs font-bold text-zinc-700 uppercase tracking-wider mb-1.5">Topic *</label>
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. How to reduce Airbnb cleaning costs in 2026"
            className="w-full px-3 py-2 text-sm border border-zinc-300 rounded-sm focus:outline-none focus:border-[#0052FF]"
            data-testid="blog-topic-input"
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-zinc-700 uppercase tracking-wider mb-1.5">SEO keywords (comma separated, optional)</label>
          <input
            type="text"
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            placeholder="airbnb cleaning fees, short term rental cleaning"
            className="w-full px-3 py-2 text-sm border border-zinc-300 rounded-sm focus:outline-none focus:border-[#0052FF]"
            data-testid="blog-keywords-input"
            disabled={submitting}
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="blog-generate-submit"
        >
          {submitting ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="bold" />}
          {submitting ? 'Starting…' : 'Generate article'}
        </button>
      </form>

      {/* Active jobs */}
      {pendingJobs.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-4" data-testid="blog-active-jobs">
          <h3 className="text-xs font-bold text-blue-900 uppercase tracking-wider mb-2">Generating…</h3>
          <div className="space-y-2">
            {pendingJobs.map(([jobId, j]) => (
              <div key={jobId} className="flex items-center gap-2 text-sm text-blue-900">
                <CircleNotch size={14} className="animate-spin" />
                <span className="truncate">{j.topic || jobId}</span>
                <span className="text-xs text-blue-700 ml-auto">{j.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Posts list */}
      <div>
        <h2 className="text-sm font-bold text-zinc-900 mb-3 flex items-center gap-2">
          <Article size={16} weight="duotone" /> Published ({posts.length})
        </h2>
        {loading ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="bg-white border border-dashed border-zinc-300 rounded-sm p-10 text-center text-sm text-zinc-500" data-testid="blog-empty">
            No articles yet. Generate your first one above.
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-100" data-testid="blog-posts-list">
            {posts.map((p) => (
              <div key={p.slug} className="flex items-start gap-4 p-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-zinc-900 truncate">{p.title}</h3>
                  <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{p.excerpt}</p>
                  <p className="text-[11px] text-zinc-400 mt-1.5">
                    /{p.slug} · {new Date(p.published_at || p.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={`/blog/${p.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 text-zinc-500 hover:text-[#0052FF] hover:bg-zinc-50 rounded-sm"
                    title="View"
                    data-testid={`blog-view-${p.slug}`}
                  >
                    <ArrowSquareOut size={16} />
                  </a>
                  <button
                    onClick={() => handleDelete(p.slug)}
                    className="p-2 text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded-sm"
                    title="Delete"
                    data-testid={`blog-delete-${p.slug}`}
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
